import { ApiError, type CameraMedia, queryKeys, type SupportTicketListItem } from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SUPPORT_TICKET_STATUS_TONE,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Paperclip, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { formatSize, MEDIA_KIND_LABEL, mediaTime } from './camera-media-grid.js'
import { useDebouncedValue } from './image-library-grid.js'

/** The footage itself. Plain `<img>`/`<video>` on a same-site URL, so the
 *  session cookie authorizes it exactly as it does for a cabinet still. */
export function MediaViewer({ media, src }: { media: CameraMedia; src: string }) {
  return media.kind === 'clip' ? (
    // biome-ignore lint/a11y/useMediaCaption: surveillance clips carry no audio or speech
    <video
      src={src}
      controls
      preload="metadata"
      className="max-h-[60vh] w-full rounded-md bg-black"
    />
  ) : (
    <img
      src={src}
      alt={`${media.cameraName} ${MEDIA_KIND_LABEL[media.kind].toLowerCase()}`}
      className="max-h-[60vh] w-full rounded-md bg-black object-contain"
    />
  )
}

/** Marks a capture the tablet took by itself, and links the order it was for. */
export function CheckoutErrorNote({ media }: { media: CameraMedia }) {
  if (media.trigger !== 'checkout_error') return null
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm">
      <Badge tone="warning">Auto · checkout error</Badge>
      {media.orderId && media.orderNumber ? (
        <Link to="/orders/$orderId" params={{ orderId: media.orderId }} className="underline">
          Order {media.orderNumber}
        </Link>
      ) : (
        <span className="text-foreground-muted">No order had been created yet</span>
      )}
    </p>
  )
}

export function mediaDescription(media: CameraMedia): string {
  return `${media.machineName} · ${media.cameraName} · ${mediaTime(media)}${
    media.sizeBytes !== null ? ` · ${formatSize(media.sizeBytes)}` : ''
  }`
}

/**
 * Opens one capture. Offers attaching it to a support ticket and, when no
 * ticket is using it, deleting it. Every view is written to the audit log.
 */
export function CameraMediaDialog({
  media,
  onClose,
}: {
  media: CameraMedia | null
  onClose: () => void
}) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [attaching, setAttaching] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/cameras/media/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.cameras.all() })
      toast.success('Capture deleted')
      setConfirmDelete(false)
      onClose()
    },
    onError: (err) => {
      toast.error('Could not delete the capture', err instanceof ApiError ? err.message : undefined)
      setConfirmDelete(false)
    },
  })

  return (
    <>
      <Dialog open={media !== null} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-3xl">
          {media ? (
            <>
              <DialogHeader>
                <DialogTitle>{MEDIA_KIND_LABEL[media.kind]}</DialogTitle>
              </DialogHeader>
              <DialogBody className="space-y-3">
                <MediaViewer media={media} src={`${apiBaseUrl}/cameras/media/${media.id}/file`} />
                <p className="text-sm text-foreground-muted">{mediaDescription(media)}</p>
                <CheckoutErrorNote media={media} />
                {media.requestedByName ? (
                  <p className="text-xs text-foreground-subtle">
                    Requested by {media.requestedByName}
                  </p>
                ) : null}
                {media.ticketCount > 0 ? (
                  <Badge tone="primary">
                    <Paperclip className="size-3" aria-hidden="true" />
                    Attached to {media.ticketCount} support ticket
                    {media.ticketCount === 1 ? '' : 's'}
                  </Badge>
                ) : null}
                <p className="text-xs text-foreground-subtle">
                  Footage may show customers. Viewing it is recorded in the audit log.
                </p>
              </DialogBody>
              <DialogFooter>
                {can('kiosks:manage') ? (
                  <Button
                    variant="ghost"
                    className="mr-auto"
                    disabled={media.ticketCount > 0}
                    title={media.ticketCount > 0 ? 'Detach it from its tickets first' : undefined}
                    onClick={() => setConfirmDelete(true)}
                  >
                    <Trash2 /> Delete
                  </Button>
                ) : null}
                {can('support:write') ? (
                  <Button variant="primary" onClick={() => setAttaching(true)}>
                    <Paperclip /> Attach to ticket
                  </Button>
                ) : null}
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <AttachToTicketDialog media={media} open={attaching} onOpenChange={setAttaching} />

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this capture?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              It is removed for good. Captures are deleted automatically after 90 days unless they
              are attached to a support ticket.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => media && remove.mutate(media.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function AttachToTicketDialog({
  media,
  open,
  onOpenChange,
}: {
  media: CameraMedia | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const q = useDebouncedValue(search.trim(), 250)
  // Closed tickets can still be reopened, but nobody attaches evidence to
  // one on purpose; a search by number still finds them.
  const filters = { q: q || undefined, pageSize: 20 }

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.supportTickets.list(filters),
    queryFn: () =>
      api.get<{ items: SupportTicketListItem[]; total: number }>('/support-tickets', {
        query: filters,
      }),
    enabled: open,
  })

  const attach = useMutation({
    mutationFn: (ticketId: string) =>
      api.post(`/support-tickets/${ticketId}/media`, { mediaId: media?.id }),
    onSuccess: async (_result, ticketId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.cameras.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.media(ticketId) }),
      ])
      toast.success('Attached to the ticket')
      onOpenChange(false)
    },
    onError: (err) =>
      toast.error('Could not attach it', err instanceof ApiError ? err.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Attach to a support ticket</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ticket number, subject or phone"
            aria-label="Search support tickets"
          />
          <div className="max-h-72 overflow-y-auto rounded-md border border-border">
            {isLoading ? (
              <p className="p-4 text-center text-sm text-foreground-muted">Loading tickets…</p>
            ) : data?.items.length ? (
              <ul className="divide-y divide-border">
                {data.items.map((ticket) => (
                  <li key={ticket.id} className="flex items-center gap-3 p-2">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 text-sm font-medium">
                        <span className="font-identifier">{ticket.ticketNumber}</span>
                        <Badge tone={SUPPORT_TICKET_STATUS_TONE[ticket.status]}>
                          {ticket.status}
                        </Badge>
                      </p>
                      <p className="truncate text-xs text-foreground-muted">{ticket.subject}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={attach.isPending && attach.variables === ticket.id}
                      onClick={() => attach.mutate(ticket.id)}
                    >
                      Attach
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-4 text-center text-sm text-foreground-muted">
                No tickets match.{' '}
                <Link to="/support-tickets" className="underline">
                  Open the ticket queue
                </Link>{' '}
                to create one first.
              </p>
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
